/**
 * OfflineUnavailable — a clear "this feature needs a connection" state for
 * pages that are NOT offline-capable (analytics, reports, dashboards). Shown
 * instead of failing network calls / empty charts when the device is offline.
 */
import type { ReactNode } from "react";
import { CloudOff } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { useOffline } from "@/offline/OfflineProvider";

export function OfflineUnavailable({ feature = "This page" }: { feature?: string }) {
  return (
    <div className="p-4 md:p-6">
      <EmptyState
        icon={CloudOff}
        title={`${feature} isn't available offline`}
        description="Reconnect to the internet to view live analytics and reports."
        className="py-12"
      />
    </div>
  );
}

/** Route guard: renders an offline notice instead of the page when the device
 *  is offline. Keeps the page's lazy chunk from even mounting offline, so it's
 *  also safe for pages that aren't offline-capable. */
export function OnlineOnly({ feature, children }: { feature: string; children: ReactNode }) {
  const { online } = useOffline();
  if (!online) return <OfflineUnavailable feature={feature} />;
  return <>{children}</>;
}
